import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Download, FileCheck2, Building2 } from "lucide-react";

const eur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v) || 0);

export default function SepaPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("draft");
  const [batches, setBatches] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [collectionDate, setCollectionDate] = useState(new Date(Date.now()+30*86400000).toISOString().slice(0,10));
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0,10));
  const [toDate, setToDate] = useState(new Date(Date.now()+60*86400000).toISOString().slice(0,10));
  const [draftBatch, setDraftBatch] = useState(null);

  const load = async () => {
    const [b, m] = await Promise.all([http.get("/sepa-batches"), http.get("/sepa-mandates")]);
    setBatches(b.data.items || []); setMandates(m.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const buildDraft = async () => {
    try {
      const { data } = await http.post("/sepa-batches/draft", {
        collection_date: collectionDate, from_date: fromDate, to_date: toDate,
      });
      setDraftBatch(data); load();
      toast.success(t("sepa.draftBuilt", { n: data.invoice_ids.length }));
    } catch (e) { toast.error("Draft failed"); }
  };

  const generate = async (bid) => {
    try {
      const { data } = await http.post(`/sepa-batches/${bid}/generate`);
      toast.success(t("sepa.generated") + " · " + data.batch_number); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.messages?.[0] || "Generate failed");
    }
  };
  const downloadXml = async (bid, name) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.access_token");
    const r = await fetch(`${base}/api/sepa-batches/${bid}/xml`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("Download failed");
    const blob = await r.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${name}.xml`; a.click();
  };
  const markSent = async (bid) => {
    const ref = prompt(t("sepa.bankRefPrompt"));
    if (ref == null) return;
    try { await http.post(`/sepa-batches/${bid}/mark-sent`, { bank_reference: ref }); toast.success("Sent"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <PageChrome title={t("sepa.title")} subtitle={t("sepa.subtitle")} testId="sepa-page">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="draft" data-testid="sepa-tab-draft">{t("sepa.draftTab")}</TabsTrigger>
          <TabsTrigger value="batches" data-testid="sepa-tab-batches">{t("sepa.batchesTab")}</TabsTrigger>
          <TabsTrigger value="mandates" data-testid="sepa-tab-mandates">{t("sepa.mandatesTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="draft">
          <Card className="p-4 mb-4">
            <div className="grid grid-cols-4 gap-3">
              <div><label className="text-xs">{t("sepa.collectionDate")}</label><Input type="date" value={collectionDate} onChange={(e)=>setCollectionDate(e.target.value)}/></div>
              <div><label className="text-xs">{t("sepa.fromDate")}</label><Input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)}/></div>
              <div><label className="text-xs">{t("sepa.toDate")}</label><Input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)}/></div>
              <div className="flex items-end"><Button onClick={buildDraft} className="bg-[#0A1F44]" data-testid="sepa-build-draft">{t("sepa.buildDraft")}</Button></div>
            </div>
          </Card>
          {draftBatch && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold">{draftBatch.batch_number} · {draftBatch.invoice_ids.length} {t("sepa.invoices")} · {eur(draftBatch.total_amount)}</div>
                <Button onClick={()=>generate(draftBatch.id)} className="bg-[#C8102E] hover:bg-[#a40c25]" data-testid="sepa-generate-btn"><FileCheck2 className="h-4 w-4 mr-1"/>{t("sepa.generate")}</Button>
              </div>
              {draftBatch.warnings?.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 rounded">
                  {draftBatch.warnings.length} {t("sepa.warningsFound")}
                </div>
              )}
              <table className="w-full text-sm mt-2">
                <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left">{t("sepa.debtor")}</th><th>{t("sepa.iban")}</th><th>{t("sepa.invoiceRef")}</th><th className="text-right">{t("invoices.total")}</th></tr></thead>
                <tbody className="divide-y">
                  {draftBatch.mandates_snapshot.map(m => (
                    <tr key={m.invoice_id}><td className="py-1">{m.debtor_name}</td><td className="font-mono text-xs">{m.debtor_iban}</td><td className="font-mono text-xs">{m.invoice_ref}</td><td className="text-right">{eur(m.amount_eur)}</td></tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="batches">
          <Card className="overflow-hidden">
            <table className="w-full text-sm" data-testid="sepa-batches-table">
              <thead className="bg-slate-50 text-xs uppercase"><tr>
                <th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">{t("sepa.collectionDate")}</th><th className="text-left px-3 py-2">{t("sepa.statusLabel")}</th>
                <th className="text-right px-3 py-2">{t("invoices.total")}</th><th className="text-right px-3 py-2">{t("sepa.nInvoices")}</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody className="divide-y">
                {batches.map(b => (
                  <tr key={b.id} data-testid={`sepa-batch-row-${b.id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{b.batch_number}</td>
                    <td className="px-3 py-2">{b.collection_date}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${b.status==='generated'?'bg-emerald-100 text-emerald-700':b.status==='sent'?'bg-sky-100 text-sky-700':b.status==='rejected'?'bg-rose-100 text-rose-700':'bg-slate-100'}`}>{b.status}</span></td>
                    <td className="px-3 py-2 text-right">{eur(b.total_amount)}</td>
                    <td className="px-3 py-2 text-right">{b.invoice_ids.length}</td>
                    <td className="px-3 py-2 text-right">
                      {b.status === "draft" && <Button size="sm" onClick={()=>generate(b.id)} className="bg-[#C8102E] hover:bg-[#a40c25] mr-1"><FileCheck2 className="h-4 w-4"/></Button>}
                      {["generated","sent","rejected"].includes(b.status) && <Button size="sm" variant="ghost" onClick={()=>downloadXml(b.id, b.batch_number)} data-testid={`sepa-xml-${b.id}`}><Download className="h-4 w-4"/></Button>}
                      {b.status === "generated" && <Button size="sm" variant="ghost" onClick={()=>markSent(b.id)}>{t("sepa.markSent")}</Button>}
                    </td>
                  </tr>
                ))}
                {batches.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400">—</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="mandates">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase"><tr><th className="text-left px-3 py-2">{t("sepa.family")}</th><th className="text-left px-3 py-2">{t("sepa.iban")}</th><th className="text-left px-3 py-2">{t("sepa.bic")}</th><th className="text-left px-3 py-2">{t("sepa.mandateId")}</th><th className="text-left px-3 py-2">{t("sepa.statusLabel")}</th></tr></thead>
              <tbody className="divide-y">
                {mandates.map(m => (
                  <tr key={m.family_id}><td className="px-3 py-2 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400"/>{m.family_name}</td><td className="px-3 py-2 font-mono text-xs">{m.iban || "—"}</td><td className="px-3 py-2">{m.bic || "—"}</td><td className="px-3 py-2 font-mono text-xs">{m.mandate_id || "—"}</td><td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${m.status==='active'?'bg-emerald-100 text-emerald-700':'bg-slate-100'}`}>{m.status}</span></td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>
    </PageChrome>
  );
}
